import React from 'react';
import { useSelector } from 'react-redux';
import { GoldIcon, SilverIcon, PlatinumIcon } from '../icons/MetalIcons';

const WizardStepThankYou = ({ onComplete, wizardData, onAddAnother }) => {
  const metalPrices = useSelector(state => state.metalPrices.prices);

  // Get metal icon component
  const getMetalIcon = () => {
    switch (wizardData.metal_type) {
      case 'gold': return <GoldIcon size={60} />;
      case 'silver': return <SilverIcon size={60} />;
      case 'platinum': return <PlatinumIcon size={60} />;
      default: return <GoldIcon size={60} />;
    }
  };

  // Get metal display name
  const getMetalName = () => {
    return wizardData.metal_type ?
      wizardData.metal_type.charAt(0).toUpperCase() + wizardData.metal_type.slice(1) :
      'Metal';
  };

  // Get weight unit display
  const getWeightDisplay = () => {
    const amount = wizardData.target_weight || 0;
    switch (wizardData.weight_type) {
      case 'g':
        return amount === 1 ? '1 Gram' : `${amount} Grams`;
      case 'oz':
        return amount === 1 ? '1 Troy Ounce' : `${amount} Troy Ounces`;
      case 'kg':
        return amount === 1 ? '1 Kilogram' : `${amount} Kilograms`;
      default:
        return `${amount} ${wizardData.weight_type}`;
    }
  };

  // Get frequency display
  const getFrequencyDisplay = () => {
    switch (wizardData.period) {
      case 'weekly': return 'weekly';
      case 'bi-weekly': return 'bi-weekly';
      case 'monthly': return 'monthly';
      case 'quarterly': return 'quarterly';
      default: return wizardData.period;
    }
  };

  // Calculate estimated months
  const calculateEstimatedMonths = () => {
    if (!metalPrices || !metalPrices[wizardData.metal_type]) return null;

    const metalData = metalPrices[wizardData.metal_type];
    let pricePerUnit;

    if (wizardData.weight_type === 'oz') {
      pricePerUnit = metalData.price_per_ounce;
    } else if (wizardData.weight_type === 'g') {
      pricePerUnit = metalData.price_per_gram;
    } else if (wizardData.weight_type === 'kg') {
      pricePerUnit = metalData.price_per_gram * 1000;
    }

    if (!pricePerUnit || pricePerUnit <= 0) return null;

    const totalCost = wizardData.target_weight * pricePerUnit * 1.25;
    const paymentsPerMonth = wizardData.period === 'weekly' ? 4 :
                             wizardData.period === 'bi-weekly' ? 2 :
                             wizardData.period === 'monthly' ? 1 : 0.33;

    return Math.ceil(totalCost / (wizardData.amount_per_period * paymentsPerMonth));
  };

  // Get estimated delivery text
  const getEstimatedDelivery = () => {
    const months = calculateEstimatedMonths();
    if (!months) return 'Calculating...';

    return `~${months} month${months > 1 ? 's' : ''}`;
  };

  // Get first contribution date
  const getFirstContributionDate = () => {
    const date = new Date();
    switch (wizardData.period) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'bi-weekly':
        date.setDate(date.getDate() + 14);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      default:
        date.setDate(date.getDate() + 14);
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: '40px 30px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px'
        }}>
          {getMetalIcon()}
        </div>
        <h1 style={{ fontSize: '24px', marginBottom: '8px', fontWeight: 'bold' }}>
          You're Stacking {getMetalName()}!
        </h1>
        <p style={{ opacity: 0.9, fontSize: '16px', margin: 0 }}>
          Your goal is active and your first contribution is scheduled
        </p>
      </div>

      {/* Timeline Banner */}
      <div style={{
        background: '#fef3c7',
        borderLeft: '4px solid #f59e0b',
        padding: '16px 20px',
        margin: '24px',
        borderRadius: '0 8px 8px 0'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#92400e',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '4px'
        }}>
          Estimated Delivery
        </div>
        <div style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#78350f'
        }}>
          {getEstimatedDelivery()}
        </div>
      </div>

      {/* Details */}
      <div style={{ padding: '0 24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>Metal</span>
          <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{getMetalName()}</span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>Target</span>
          <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{getWeightDisplay()}</span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: '1px solid #f3f4f6'
        }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>Contribution</span>
          <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
            ${wizardData.amount_per_period?.toFixed(2)} {getFrequencyDisplay()}
          </span>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 0'
        }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>First Contribution</span>
          <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{getFirstContributionDate()}</span>
        </div>
      </div>

      {/* Next Steps */}
      <div style={{
        background: '#f9fafb',
        padding: '24px',
        marginTop: '20px'
      }}>
        <h3 style={{ fontSize: '14px', color: '#374151', marginBottom: '16px', fontWeight: '600' }}>
          What happens next
        </h3>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '24px',
            height: '24px',
            background: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            flexShrink: 0,
            color: '#065f46',
            fontWeight: '500'
          }}>1</div>
          <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', margin: 0 }}>
            We'll process your first contribution on your scheduled date
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '24px',
            height: '24px',
            background: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            flexShrink: 0,
            color: '#065f46',
            fontWeight: '500'
          }}>2</div>
          <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', margin: 0 }}>
            Track your progress on your dashboard as your balance grows
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '24px',
            height: '24px',
            background: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            flexShrink: 0,
            color: '#065f46',
            fontWeight: '500'
          }}>3</div>
          <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', margin: 0 }}>
            When you hit your target, we'll source and ship your {wizardData.metal_type}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '24px' }}>
        <button
          onClick={onComplete}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          View My Stack →
        </button>
        {onAddAnother && (
          <button
            onClick={onAddAnother}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: 'transparent',
              color: '#6b7280',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              marginTop: '8px'
            }}
          >
            Add Another Goal
          </button>
        )}
      </div>
    </>
  );
};

export default WizardStepThankYou;
